import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowRight,
  Briefcase,
  Truck,
  Landmark,
  Home as HomeIcon,
  FileText,
  CircleDollarSign,
  ShieldCheck,
  Phone,
  Mail,
  MapPin,
  Check,
  ChevronDown,
  Lock,
  Star,
  Zap,
  Sliders,
  Users,
  BadgeDollarSign,
} from "lucide-react";
import CFLogo from "@/components/CFLogo";
import ChatWidget from "@/components/ChatWidget";
import PreQualify from "@/components/PreQualify";
import { HOME } from "@/constants/testIds";
import { APPLY_LINK_PROPS } from "@/constants/links";
import { extractAxiosError } from "@/lib/api-errors";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate the same expected initial doc labels the backend pre-populates,
// so the apply form can offer matching upload slots up-front.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const expectedApplyDocs = () => {
  const today = new Date();
  const docs = [];
  for (let i = 1; i <= 4; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    docs.push(`Bank statement — ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`);
  }
  if (today.getDate() >= 15) {
    docs.push(`Month-to-date statement — ${MONTH_NAMES[today.getMonth()]} ${today.getFullYear()}`);
  }
  return docs;
};

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
const PRODUCTS = [
  { key: "working-capital",    slug: "working-capital",     title: "Working Capital",          desc: "Flexible capital from $25K–$5M to handle payroll, inventory, marketing, or growth.", icon: CircleDollarSign, testId: HOME.productCardWorkingCapital },
  { key: "equipment",          slug: "equipment-financing", title: "Equipment Financing",      desc: "Finance trucks, machinery, medical, and tech up to $2M. 100% financing available.",  icon: Truck,             testId: HOME.productCardEquipment },
  { key: "sba",                slug: "sba-loans",           title: "SBA Loans",                desc: "SBA 7(a) and 504 programs up to $5M. Long terms, low rates, government-backed.",     icon: Landmark,          testId: HOME.productCardSba },
  { key: "heloc",              slug: "heloc",               title: "HELOC",                    desc: "Tap your home equity to fuel business growth. Lines from $50K to $500K.",            icon: HomeIcon,          testId: HOME.productCardHeloc },
  { key: "factoring",          slug: "invoice-factoring",   title: "Invoice Factoring",        desc: "Turn unpaid invoices into same-day cash. Advance up to 95% on B2B receivables.",     icon: FileText,          testId: HOME.productCardFactoring },
  { key: "loc",                slug: "line-of-credit",      title: "Business Line of Credit",  desc: "Revolving capital up to $1M. Pay interest only on what you draw.",                   icon: Briefcase,         testId: HOME.productCardLoc },
];

const STEPS = [
  { n: "1", t: "Apply in 4 minutes", d: "Tell us about your business. No credit pull required." },
  { n: "2", t: "Speak with an advisor", d: "A real person reviews your file and walks you through your options." },
  { n: "3", t: "Track everything online", d: "Watch your status, upload docs, and chat with your advisor in your portal." },
  { n: "4", t: "Funds wired", d: "Sign and we wire — most files fund same- or next-day." },
];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
const Header = () => (
  <header className="sticky top-0 z-50 backdrop-blur-md bg-cf-cream/85 border-b border-cf-line/60">
    <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
      <CFLogo testId={HOME.headerLogo} />
      <nav className="hidden lg:flex items-center gap-9 text-sm text-cf-ink-soft">
        <a data-testid={HOME.navProducts} href="#products" className="cf-link-underline hover:text-cf-ink transition">Funding</a>
        <a data-testid="nav-about" href="#about" className="cf-link-underline hover:text-cf-ink transition">About</a>
        <a data-testid={HOME.navProcess} href="#process" className="cf-link-underline hover:text-cf-ink transition">How it works</a>
        <a href="tel:+17377757077" className="cf-link-underline hover:text-cf-ink transition flex items-center gap-1.5">
          <Phone size={13} strokeWidth={1.8} /> (737) 775-7077
        </a>
      </nav>
      <div className="flex items-center gap-2 md:gap-3">
        <Link
          to="/merchant/login"
          data-testid={HOME.merchantLogin}
          className="hidden sm:inline-block text-xs tracking-[0.16em] uppercase text-cf-ink-soft hover:text-cf-ink transition cf-link-underline"
        >
          Merchant portal
        </Link>
        <Link
          to="/agent/login"
          data-testid={HOME.agentLogin}
          className="hidden md:inline-block text-xs tracking-[0.16em] uppercase text-cf-ink-soft hover:text-cf-gold transition cf-link-underline"
        >
          Agent
        </Link>
        <a {...APPLY_LINK_PROPS} data-testid={HOME.navApplyBtn} className="cf-btn-primary text-sm">
          Apply now <ArrowRight size={15} strokeWidth={2} />
        </a>
      </div>
    </div>
  </header>
);

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const Hero = () => (
  <section className="relative cf-soft-rays overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-20 md:pb-28 text-center max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-cf-line rounded-full mx-auto"
        data-testid={HOME.heroDirectLenderBadge}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-cf-gold" />
        <span className="text-xs uppercase tracking-[0.18em] text-cf-ink-soft font-medium">
          Direct lender · Trusted by small businesses
        </span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="font-serif text-cf-ink mt-7 mx-auto max-w-3xl"
        style={{
          fontSize: "clamp(2.4rem, 5.8vw, 4.8rem)",
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          fontWeight: 500,
        }}
        data-testid={HOME.heroHeadline}
      >
        Move your business <span className="italic text-cf-gold">forward</span> with funding built for you.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="mt-6 mx-auto max-w-2xl text-base md:text-lg text-cf-ink-soft leading-relaxed"
      >
        Capless Funding is a direct lender. Apply once and unlock Working Capital,
        Equipment, SBA, HELOC, Invoice Factoring or Lines of Credit — with a portal
        to track your progress every step of the way.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-10 flex flex-wrap items-center justify-center gap-4"
      >
        <a {...APPLY_LINK_PROPS} className="cf-btn-gold" data-testid="hero-cta-apply">
          Apply now <ArrowRight size={16} strokeWidth={2} />
        </a>
        <a href="tel:+17377757077" className="cf-btn-ghost-light" data-testid={HOME.heroTalkBtn}>
          <Phone size={15} strokeWidth={1.8} /> Talk to an advisor
        </a>
      </motion.div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm text-cf-ink-soft">
        {["No credit pull at all", "No credit score required", "Same-day funding"].map((t) => (
          <div key={t} className="flex items-center gap-2">
            <Check size={16} strokeWidth={2} className="text-cf-gold" />
            {t}
          </div>
        ))}
      </div>

      {/* Pre-qualify widget */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.45 }}
        className="mt-14 max-w-3xl mx-auto text-left"
      >
        <PreQualify />
      </motion.div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------
const Products = () => (
  <section id="products" className="py-20 md:py-28 bg-cf-paper border-y border-cf-line">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-3xl mx-auto text-center">
        <span className="cf-overline">Funding Solutions</span>
        <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
          One application. <span className="italic text-cf-gold">Six ways</span> to fund your business.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {PRODUCTS.map((p) => {
          const Icon = p.icon;
          return (
            <Link
              key={p.key}
              to={`/funding/${p.slug}`}
              data-testid={p.testId}
              className="cf-card-light p-7 md:p-8 block group"
            >
              <div className="w-12 h-12 rounded-2xl bg-cf-mint flex items-center justify-center mb-5">
                <Icon size={22} strokeWidth={1.6} className="text-cf-ink" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-cf-ink tracking-tight">{p.title}</h3>
              <p className="text-cf-slate text-[0.95rem] leading-relaxed mt-3">{p.desc}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-cf-ink cf-link-underline">
                Learn more <ArrowRight size={14} strokeWidth={2} />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// About Us
// ---------------------------------------------------------------------------
const ABOUT_ITEMS = [
  {
    icon: Zap,
    title: "Simple Process",
    desc: "Getting funds through Capless Funding is fast and simple. You can apply in minutes and receive funds the same day as approval.†",
  },
  {
    icon: Sliders,
    title: "Tailored Options",
    desc: "We offer a business term loan and a business line of credit to help meet your unique funding needs, and can tailor your repayment terms to fit your business.",
  },
  {
    icon: ShieldCheck,
    title: "Digital Security",
    desc: "Capless Funding is fully dedicated to protecting the safety and security of our customers and their data. Visit our security center to see how we keep your information safe.",
  },
  {
    icon: Users,
    title: "Real People",
    desc: "As a Capless Funding customer, you'll get a dedicated U.S.-based loan advisor (available five days a week) to help you through the entire process.",
  },
  {
    icon: BadgeDollarSign,
    title: "Transparent Pricing",
    desc: "Make informed decisions with our Capless Funding agents. It gives you a comprehensive breakdown of your cost of capital — including all the key terms you need to evaluate potential loan offers.",
  },
];

const About = () => (
  <section id="about" className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <span className="cf-overline">About us</span>
        <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
          We&apos;re here to help you succeed <span className="italic text-cf-gold">on your own terms</span>.
        </h2>
        <p className="text-cf-ink-soft text-base md:text-lg leading-relaxed mt-6">
          Capless Funding was built around a simple idea: small business owners deserve a
          lender that treats them like partners — not paperwork. Here&apos;s what that looks
          like in practice.
        </p>
        <div className="hidden lg:block mt-10 p-5 rounded-2xl bg-cf-mint/60 border border-cf-mint">
          <div className="flex items-center gap-3 text-cf-ink font-semibold">
            <Phone size={15} strokeWidth={1.8} className="text-cf-gold" />
            Talk to a real advisor
          </div>
          <p className="text-cf-slate text-sm mt-1">
            <a href="tel:+17377757077" className="text-cf-ink font-medium cf-link-underline">(737) 775-7077</a> · Mon–Fri, 9 a.m. – 7 p.m. ET
          </p>
        </div>
      </div>

      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
        {ABOUT_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} className="cf-card-light p-6 md:p-7">
              <div className="w-11 h-11 rounded-2xl bg-cf-mint flex items-center justify-center mb-4">
                <Icon size={20} strokeWidth={1.7} className="text-cf-ink" />
              </div>
              <h3 className="font-serif text-lg md:text-xl text-cf-ink tracking-tight">{item.title}</h3>
              <p className="text-cf-slate text-sm leading-relaxed mt-2">{item.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Process
// ---------------------------------------------------------------------------
const Process = () => (
  <section id="process" className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-2xl mx-auto text-center">
        <span className="cf-overline">How it works</span>
        <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
          A clear, friendly <span className="italic text-cf-gold">funding journey</span>.
        </h2>
      </div>
      <div className="grid md:grid-cols-4 gap-8 mt-14">
        {STEPS.map((s) => (
          <div key={s.n}>
            <div className="w-11 h-11 rounded-full bg-cf-ink text-white font-serif text-lg flex items-center justify-center mb-5">
              {s.n}
            </div>
            <h3 className="font-serif text-xl text-cf-ink tracking-tight">{s.t}</h3>
            <p className="text-cf-slate text-sm leading-relaxed mt-2">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Apply (with merchant account creation)
// ---------------------------------------------------------------------------
const Apply = () => {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    funding_amount: "", product: "Working Capital",
    monthly_revenue: "", time_in_business: "",
    password: "", notes: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
  // Files keyed by expected-doc label, e.g. {"Bank statement — May 2026": File}
  const [files, setFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const docLabels = React.useMemo(() => expectedApplyDocs(), []);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setFile = (label, file) => setFiles((prev) => ({ ...prev, [label]: file }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setStatus({ loading: false, ok: false, error: "Please choose a password of at least 6 characters." });
      return;
    }
    setStatus({ loading: true, ok: false, error: "" });
    setUploadProgress({ done: 0, total: 0 });
    try {
      // 1) Create the lead + merchant account (this also sets the auth cookie).
      const { data: lead } = await axios.post(`${API}/leads`, form, { withCredentials: true });

      // 2) Upload any bank statements the user attached. We match by doc label.
      const required = lead.required_documents || [];
      const tasks = [];
      for (const slot of required) {
        const f = files[slot.name];
        if (!f) continue;
        const fd = new FormData();
        fd.append("file", f);
        fd.append("lead_id", lead.id);
        tasks.push(
          axios.post(`${API}/merchant/documents/${slot.id}/upload`, fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          }),
        );
      }
      if (tasks.length > 0) {
        setUploadProgress({ done: 0, total: tasks.length });
        let done = 0;
        await Promise.all(
          tasks.map((p) =>
            p.then(() => {
              done += 1;
              setUploadProgress({ done, total: tasks.length });
            }),
          ),
        );
      }

      setStatus({ loading: false, ok: true, error: "" });
      await refreshMe();
      setTimeout(() => navigate("/merchant/dashboard"), 900);
    } catch (err) {
      setStatus({
        loading: false, ok: false,
        error: extractAxiosError(err, "Something went wrong. Please try again."),
      });
    }
  };

  return (
    <section id="apply" className="py-20 md:py-28 bg-cf-paper border-y border-cf-line">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="cf-overline">Apply for funding</span>
          <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
            We&apos;re ready when <span className="italic text-cf-gold">you are</span>.
          </h2>
          <p className="text-cf-ink-soft text-base leading-relaxed mt-5">
            Apply once and instantly get your own portal to upload your last 4 months of bank statements, track status, and chat with your advisor.
          </p>
        </div>

        <form
          data-testid={HOME.applyForm}
          onSubmit={submit}
          className="bg-white rounded-3xl border border-cf-line p-8 md:p-10 shadow-[0_30px_70px_-30px_rgba(15,37,64,0.18)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Business name</label>
              <input data-testid={HOME.applyBusinessName} required value={form.business_name} onChange={update("business_name")} className="cf-input-light" placeholder="Acme Logistics LLC" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Your name</label>
              <input data-testid={HOME.applyContactName} required value={form.contact_name} onChange={update("contact_name")} className="cf-input-light" placeholder="Jordan Cole" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Email</label>
              <input data-testid={HOME.applyEmail} required type="email" value={form.email} onChange={update("email")} className="cf-input-light" placeholder="jordan@acme.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Phone</label>
              <input data-testid={HOME.applyPhone} required value={form.phone} onChange={update("phone")} className="cf-input-light" placeholder="(212) 555-0190" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Amount needed</label>
              <input data-testid={HOME.applyAmount} required value={form.funding_amount} onChange={update("funding_amount")} className="cf-input-light" placeholder="$250,000" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Funding program</label>
              <div className="relative">
                <select data-testid={HOME.applyProduct} required value={form.product} onChange={update("product")} className="cf-input-light appearance-none pr-10 cursor-pointer">
                  {PRODUCTS.map((p) => (
                    <option key={p.key} value={p.title}>{p.title}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-cf-ink-soft pointer-events-none" size={16} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Monthly revenue</label>
              <input data-testid={HOME.applyRevenue} value={form.monthly_revenue} onChange={update("monthly_revenue")} className="cf-input-light" placeholder="$85,000" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Time in business</label>
              <input data-testid={HOME.applyTib} value={form.time_in_business} onChange={update("time_in_business")} className="cf-input-light" placeholder="3 years" />
            </div>
            <div className="sm:col-span-2 rounded-2xl bg-cf-mint/50 border border-cf-mint p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lock size={15} strokeWidth={1.8} className="text-cf-ink" />
                <span className="text-sm font-semibold text-cf-ink">Create your merchant portal</span>
              </div>
              <p className="text-xs text-cf-slate mb-4">
                Choose a password — you&apos;ll use it with your email to sign in to your portal and track your application, upload documents, and chat with your advisor.
              </p>
              <input
                data-testid={HOME.applyPassword}
                required type="password" value={form.password} onChange={update("password")}
                className="cf-input-light" placeholder="Choose a password (min 6 chars)" autoComplete="new-password"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Anything else we should know? (optional)</label>
              <textarea data-testid={HOME.applyNotes} value={form.notes} onChange={update("notes")} rows={3} className="cf-input-light resize-none" placeholder="Use case, timing, anything that helps us help you faster." />
            </div>

            {/* Bank statements — inline upload */}
            <div data-testid="apply-docs-section" className="sm:col-span-2 rounded-2xl bg-white border border-cf-gold/40 p-5 md:p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-cf-mint flex items-center justify-center shrink-0">
                  <FileText size={16} strokeWidth={1.8} className="text-cf-ink" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-cf-ink">Attach your bank statements</div>
                  <p className="text-xs text-cf-slate mt-1 leading-relaxed">
                    Last 4 months from your business checking account
                    {docLabels.length === 5 && " plus the current month-to-date (since it's past the 15th)"}.
                    PDF, JPG or PNG. 10 MB max per file. Attaching now means we can review your file the same day — you can also upload them later from your portal.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {docLabels.map((label) => {
                  const f = files[label];
                  return (
                    <label
                      key={label}
                      data-testid={`apply-doc-slot-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                        f ? "border-emerald-300 bg-emerald-50" : "border-cf-line bg-cf-paper hover:border-cf-gold/60"
                      }`}
                    >
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => setFile(label, e.target.files?.[0] || null)}
                      />
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${f ? "bg-emerald-600 text-white" : "bg-cf-line text-cf-ink"}`}>
                        {f ? <Check size={14} strokeWidth={2.5} /> : <FileText size={13} strokeWidth={1.8} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-cf-ink truncate">{label}</div>
                        <div className="text-[0.7rem] text-cf-slate truncate">
                          {f ? f.name : "Tap to choose file (optional now)"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {status.loading && uploadProgress.total > 0 && (
            <div data-testid="apply-upload-progress" className="mt-6 p-4 rounded-xl bg-cf-mint border border-cf-gold/30 text-cf-ink text-sm">
              Uploading {uploadProgress.done} of {uploadProgress.total} {uploadProgress.total === 1 ? "file" : "files"}…
            </div>
          )}

          {status.error && (
            <div data-testid={HOME.applyError} className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {status.error}
            </div>
          )}
          {status.ok && (
            <div data-testid={HOME.applySuccess} className="mt-6 p-4 rounded-xl bg-cf-mint border border-cf-gold/30 text-cf-ink text-sm">
              ✓ Application submitted! Redirecting you to your portal…
            </div>
          )}

          <button
            data-testid={HOME.applySubmit}
            type="submit" disabled={status.loading}
            className="cf-btn-gold w-full justify-center mt-7 disabled:opacity-50"
          >
            {status.loading ? "Submitting..." : <>Submit & open my portal <ArrowRight size={16} strokeWidth={2} /></>}
          </button>
          <p className="text-cf-slate text-xs mt-4 leading-relaxed text-center">
            By submitting you authorize Capless Funding to contact you about your application.
            We do not pull your credit at any point — no soft pull, no hard pull, ever.
          </p>
          <p className="text-cf-slate text-xs mt-3 text-center">
            Already applied? <Link to="/merchant/login" className="text-cf-ink font-semibold cf-link-underline">Sign in to your portal</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Testimonials
// ---------------------------------------------------------------------------
const TESTIMONIALS = [
  {
    quote:
      "Capless walked me through every step. Funds were in my account two days after I applied — couldn't ask for more.",
    name: "Daniel R.",
    role: "Owner · Inland Freight Co.",
  },
  {
    quote:
      "It felt like a partnership, not a transaction. They understood our business and structured the right product for us.",
    name: "Anika P.",
    role: "Founder · Cove Hospitality Group",
  },
  {
    quote:
      "Super easy process. My advisor was patient with my questions and explained everything clearly. Highly recommend.",
    name: "Marcus W.",
    role: "Operator · Westside Auto Services",
  },
];

const Testimonials = () => (
  <section className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-2xl mx-auto text-center">
        <span className="cf-overline">Loved by business owners</span>
        <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
          What our <span className="italic text-cf-gold">customers</span> say.
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {TESTIMONIALS.map((t) => (
          <div key={t.name} className="cf-card-light p-8">
            <div className="flex items-center gap-1 mb-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={16} className="fill-cf-gold text-cf-gold" strokeWidth={0} />
              ))}
            </div>
            <p className="text-cf-ink text-[1rem] leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
            <div className="mt-6 pt-5 border-t border-cf-line">
              <div className="text-cf-ink font-semibold text-sm">{t.name}</div>
              <div className="text-cf-slate text-xs mt-1">{t.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
const Footer = () => (
  <footer className="bg-cf-paper border-t border-cf-line">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-6">
        <CFLogo testId="footer-logo" />
        <p className="text-cf-ink-soft text-sm leading-relaxed mt-5 max-w-md">
          Capless Funding is a direct lender helping small businesses access the capital they need — quickly, and on terms that fit.
        </p>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-cf-ink-soft text-sm">
          <div className="flex items-center gap-2"><Phone size={14} strokeWidth={1.6} className="text-cf-gold" /> (737) 775-7077</div>
          <div className="flex items-center gap-2"><Mail size={14} strokeWidth={1.6} className="text-cf-gold" /> info@caplessfunding.com</div>
          <div className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.6} className="text-cf-gold" /> New York, NY</div>
        </div>
      </div>
      <div className="md:col-span-3">
        <div className="cf-overline mb-4">Funding</div>
        <ul className="space-y-2 text-cf-ink-soft text-sm">
          {PRODUCTS.map((p) => (<li key={p.key}><Link to={`/funding/${p.slug}`} className="hover:text-cf-ink transition">{p.title}</Link></li>))}
        </ul>
      </div>
      <div className="md:col-span-3">
        <div className="cf-overline mb-4">Sign in</div>
        <ul className="space-y-2 text-cf-ink-soft text-sm">
          <li><Link to="/merchant/login" className="hover:text-cf-ink transition">Merchant portal</Link></li>
          <li><Link to="/agent/login" className="hover:text-cf-ink transition">Agent console</Link></li>
        </ul>
        <div className="cf-overline mt-6 mb-4">Trust</div>
        <div className="flex items-center gap-2 text-cf-ink-soft text-sm">
          <ShieldCheck size={14} strokeWidth={1.6} className="text-cf-gold" /> Direct lender · Equal Opportunity
        </div>
      </div>
    </div>
    <div className="border-t border-cf-line">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 text-xs text-cf-slate text-center">
        © {new Date().getFullYear()} Capless Funding. All rights reserved.
      </div>
    </div>
  </footer>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const Home = () => (
  <div className="bg-cf-cream text-cf-ink font-sans">
    <Header />
    <Hero />
    <Products />
    <About />
    <Process />
    <Apply />
    <Testimonials />
    <Footer />
    <ChatWidget />
  </div>
);

export default Home;
